<template>
    <section class="padded-container">
        <TVERendezvousSpinner v-if="partials.TVERendezvousSpinner.show"></TVERendezvousSpinner>

        <TVERendezvousCode
            v-if="partials.TVERendezvousCode.show"
            :pageAttributes="pageAttr"
            :errorMessage="errorMessage"
            @regCode="handleEmitRegCode"></TVERendezvousCode>

        <TVERendezvousProviders
            v-if="partials.TVERendezvousProviders.show"
            :tier1Providers="tier1Providers"
            :tier2Providers="tier2Providers"
            @selected="handleProviderSelected"></TVERendezvousProviders>

        <TVERendezvousConfirmation
            v-if="partials.TVERendezvousConfirmation.show"></TVERendezvousConfirmation>
    </section>
</template>

<script>
    // partials
    import TVERendezvousSpinner from 'vue/components/tveRendezvous/TVERendezvousSpinner';
    import TVERendezvousCode from 'vue/components/tveRendezvous/TVERendezvousCode';
    import TVERendezvousProviders from 'vue/components/tveRendezvous/TVERendezvousProviders';
    import TVERendezvousConfirmation from 'vue/components/tveRendezvous/TVERendezvousConfirmation';
    // tracking
    import { tracking } from 'aa/vue/services/Tracking';
    // http service
    import { HttpServiceAdobeClientlessAPI } from 'aa/vue/services/HttpServiceAdobeClientlessAPI';
    // cookies
    import { getCookie, removeCookie, setCookie } from 'helpers/VanillaCookie';
    // adobe enabler
    import AdobeEnabler from 'components/livetv/AdobeEnabler';
    import { createTVProviderObj } from 'POJOs/TVProvider';

    import { mapState, mapGetters } from 'vuex';

    // constants
    const DOMAIN = 'cbs.com';

    const ENDPOINT = {
        PROVIDERS_ENDPOINT: '/live-tv/stream/tveverywhere/mvpd-configs/',
    };

    // component
    export default {
        name: 'TVERendezvous',
        data: function () {
            return {
                http: new HttpServiceAdobeClientlessAPI(),
                requestorId: new AdobeEnabler().config.requestorID,

                // code
                regCode: '',
                deviceId: '',

                // code error
                errorMessage: '',

                // providers
                providerID: null, // mso_id
                providers: [], // from clientless API
                providerConfigs: [], // from cms
                tier1Providers: [],
                tier2Providers: [],

                // view's component state (which component to display)
                partials: {
                    TVERendezvousSpinner: {
                        show: true,
                    },
                    TVERendezvousCode: {
                        show: false,
                    },
                    TVERendezvousProviders: {
                        show: false,
                    },
                    TVERendezvousConfirmation: {
                        show: false,
                    },
                },
            };
        },
        mounted() {
            this.setup();
        },
        computed: {
            ...mapState('flow', {
                flow: (state) => state.config,
            }),

            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes,
            }),

            ...mapState('user', {
                regID: (state) => state.regID,
                userStatus: (state, getters) => {
                    return getters.trackingUserStatus;
                },
                userType: (state, getters) => {
                    return getters.trackingUserType;
                },
            }),

            ...mapGetters('tve', {
                REGGIE_FQDN: 'getTveApiHost',
                SP_FQDN: 'getTveApiHost',
            }),
        },
        methods: {
            //
            // toggle components
            //

            think() {
                this.show('TVERendezvousSpinner');
            },
            show(partialsKey) {
                this.__hideAll();
                this.partials[partialsKey].show = true;
            },
            __hideAll() {
                Object.keys(this.partials).forEach((key) => {
                    this.partials[key].show = false;
                });
            },

            __killFlow() {
                let killUri = document.location.href + 'kill/';
                this.http.doGet(killUri);
            },

            //
            // tracking
            //

            __trackCodeView() {
                let obj = {
                    pageType: 'activation',
                    ottActivationPartner: this.flow.type,
                };
                tracking.trackState(obj);
                console.log('__trackCodeView', obj);
            },

            __trackCodeEntered() {
                let obj = {
                    activationShortCode: this.regCode,
                };
                tracking.trackAction('trackActivation', obj);
                console.log('__trackCodeEntered', obj);
            },

            __trackCodeError() {
                let obj = {
                    activationShortCode: this.regCode,
                    activationShortCodeFailure: '1',
                };
                tracking.trackAction('trackActivationFailure', obj);
                console.log('__trackCodeError', obj);
            },

            __trackProviderView() {
                let obj = {
                    activationShortCode: this.regCode,
                    ottActivationPartner: this.flow.type,
                    pageType: 'tv-provider',
                };
                tracking.trackState(obj);
                console.log('__trackProviderView', obj);
            },

            __trackProviderSelected() {
                let obj = {
                    mvpdPartner: this.__getProviderNameByProviderID(),
                    mvpdPartnerId: this.providerID,
                };
                tracking.trackAction('trackRendezvousTvProvider', obj);
                console.log('__trackProviderSelected', obj);
            },

            __trackConfirmation() {
                let obj = {
                    mvpdPartner: this.__getProviderNameByProviderID(),
                    mvpdPartnerId: this.providerID,
                    mvpdEventSuccess: '1',
                    userRegType: 'MVPD_rendezvous',
                    pageName: 'MVPD_RendezvousSuccess',
                    pageType: 'MVPD_RendSuccess',
                };
                tracking.trackState(obj);
                console.log('__trackConfirmation', obj);
            },

            __getProviderNameByProviderID() {
                let arr = this.tier1Providers.concat(this.tier2Providers);
                let selected = arr.filter((obj) => {
                    return obj.providerID === this.providerID;
                });
                if (selected.length) {
                    return selected[0].providerName;
                } else {
                    return null;
                }
            },

            //
            // setup
            //

            setup() {
                this.think();
                this.setVarsByCookies();
                this.checkAuthenticationFlow();
                this.prefetchProviders();
            },

            setVarsByCookies() {
                this.regCode = getCookie('regCode') ?? '';
                this.providerID = getCookie('providerID') ?? '';
            },

            prefetchProviders() {
                this.provideMVPDList();
            },

            __mapProviderMVPDListData(data) {
                let mappedData = data.map(function (obj) {
                    let newObj = {};
                    Object.keys(obj).forEach((key) => {
                        let keyName = key;
                        if (keyName === 'id') {
                            keyName = 'ID';
                        }
                        newObj[keyName] = obj[key].value;
                    });
                    return newObj;
                });
                this.__handleProvideMVPDListResponse(mappedData);
            },

            __handleProvideMVPDListResponse(providers) {
                if (providers) {
                    this.providers = providers;
                    this.http
                        .doGet(ENDPOINT.PROVIDERS_ENDPOINT)
                        .then((res) => {
                            if (res && res.success) {
                                this.providerConfigs = res.mvpdConfigList;
                                this.__createTierProviderLists();
                            }
                        })
                        .catch((err) => {
                            console.error('__handleProvideMVPDListResponse', err);
                        });
                }
            },

            __createTierProviderLists() {
                for (let p of this.providers) {
                    let tvProvider = createTVProviderObj(p, this.providerConfigs);
                    if (tvProvider) {
                        if (tvProvider.tier === 1) {
                            this.tier1Providers.push(tvProvider);
                        } else if (tvProvider.tier === 2) {
                            this.tier2Providers.push(tvProvider);
                        }
                    }
                }
            },

            //
            // reg code handler
            //

            handleEmitRegCode(obj) {
                this.think();
                this.regCode = obj.regCode.toUpperCase();
                this.__trackCodeEntered();

                // validates the reg code
                this.returnRegistrationRecord();
            },

            //
            // provider handlers
            //

            handleProviderSelected(obj) {
                this.providerID = obj.providerID;
                this.setCookie('regCode', this.regCode);
                this.setCookie('providerID', this.providerID);
                this.__trackProviderSelected();
                this.initiateAuthentication();
            },

            setCookie(cookieName, cookieValue) {
                if (cookieName) {
                    setCookie(cookieName, cookieValue, {
                        path: '/',
                        domain: '.cbs.com',
                    });
                }
            },

            removeCookie(cookieName) {
                if (cookieName) {
                    removeCookie(cookieName, {
                        path: '/',
                        domain: '.cbs.com',
                    });
                }
            },

            //
            // Clientless API steps
            // http://tve.helpdocsonline.com/clientless-api-reference
            //

            getBaseUrl() {
                return (
                    document.location.protocol +
                    '//' +
                    document.location.host +
                    document.location.pathname
                );
            },

            returnRegistrationRecord() {
                let url = this.getBaseUrl() + 'return-registration-record/?regCode=' + this.regCode;
                this.http
                    .doGet(url)
                    .then((res) => {
                        this.__handleReturnRegistrationRecordResponse(res);
                    })
                    .catch((err) => {
                        console.error('returnRegistrationRecord err', err);
                        this.errorMessage = err.message;
                    });
            },

            __handleReturnRegistrationRecordResponse(res) {
                if (res) {
                    if (res.success) {
                        this.__trackProviderView();
                        this.deviceType = res.deviceType;
                        this.deviceId = res.deviceId;
                        this.show('TVERendezvousProviders');
                    } else {
                        this.__trackCodeError();
                        this.errorMessage = this.pageAttr.tve_rendezvous_code_error_message;
                        this.show('TVERendezvousCode');
                    }
                }
            },

            provideMVPDList() {
                let url = this.SP_FQDN + '/api/v1/config/CBS?format=json'; // &deviceType=Roku
                this.http
                    .doGet(url)
                    .then((res) => {
                        this.__mapProviderMVPDListData(res.requestor.mvpds.mvpd);
                    })
                    .catch((err) => {
                        console.error('provideMVPDList err', err);
                    });
            },

            initiateAuthentication() {
                this.think();

                // redirect to authenticate with mvpd provider
                document.location.href =
                    this.REGGIE_FQDN +
                    '/api/v1/authenticate' +
                    '?reg_code=' +
                    this.regCode +
                    '&domain_name=' +
                    DOMAIN +
                    '&mso_id=' +
                    this.providerID +
                    '&requestor_id=' +
                    this.requestorId +
                    '&noflash=true' +
                    '&device_type=' +
                    this.deviceType +
                    '&device_id=' +
                    this.deviceId +
                    // redirect_url must be last
                    '&redirect_url=' +
                    encodeURI(document.location.href);
            },

            checkAuthenticationFlow() {
                if (this.regCode !== '' && this.providerID !== '') {
                    // CBS AS A PROXY TO AUTHN
                    let urlToLocal =
                        this.getBaseUrl() +
                        'checkauthn/?reg_code=' +
                        this.regCode +
                        '&providerID=' +
                        this.providerID;
                    this.http
                        .doGet(urlToLocal)
                        .then((res) => {
                            this.__handleCheckAuthenticationFlow(res);
                        })
                        .catch((err) => {
                            console.error('checkAuthenticationFlow err', err);
                        });
                } else {
                    this.__handleCheckAuthenticationFlow({
                        success: false,
                    });
                }
            },

            __handleCheckAuthenticationFlow(res) {
                if (res.success) {
                    this.__trackConfirmation();
                    this.removeCookie('regCode');
                    this.removeCookie('providerID');
                    this.show('TVERendezvousConfirmation');
                    this.__killFlow();
                } else {
                    this.__trackCodeView();
                    this.show('TVERendezvousCode');
                }
            },

        },
        components: {
            TVERendezvousSpinner,
            TVERendezvousCode,
            TVERendezvousProviders,
            TVERendezvousConfirmation,
        },
    };
</script>
